﻿/**
 * AndDone Core Class
 * Main entry point for the AndDone SDK.
 * Coordinates fragments, orchestrator, and events.
 */

// Modules
import { Fragments } from './modules/Fragments';
import { Orchestrator } from './modules/Orchestrator';
import { Events } from './modules/Events';
import { StateManagement } from './modules/StateManagement';
import { FragmentRegistry } from './modules/fragment-registry';

// Shared types and assets
import type {
  EventListenerFor,
  EventType,
  Fragment,
  FragmentName,
  InitConfig,
  PaymentMethod,
} from '@anddone/ajs-util';
import { createLogger } from '@anddone/ajs-logger';

const logger = createLogger('SDK', 'Root');

export class AndDone {
  public readonly fragments: Fragments;
  public readonly events: Events;

  // TC39 private fields prevent console access via window.AnddoneJS.instance.dataState etc.
  readonly #dataState: StateManagement;
  readonly #orchestrator: Orchestrator;
  readonly #fragmentRegistry: FragmentRegistry;

  #initialized = false;

  /**
   * @param config - Initialization configuration sent from consuming application
   */
  constructor(config: InitConfig) {
    // Initialize fragment registry.
    this.#fragmentRegistry = new FragmentRegistry();

    // Initialize events (provides hooks for consumers).
    this.events = new Events();

    // Initialize all config settings and shared SDK state.
    this.#dataState = new StateManagement(config, this.#fragmentRegistry, this.events);

    // Initialize the orchestrator (handles communication and API calls).
    this.#orchestrator = new Orchestrator(this.#dataState, this.#fragmentRegistry);

    // Initialize fragments (manages fragment lifecycle).
    this.fragments = new Fragments(
      this.#dataState,
      this.#orchestrator,
      this.events,
      this.#fragmentRegistry,
    );

    logger.info('init', `Root AndDone library loaded`);

    void this.initialize();
  }

  /**
   * Async initialization sequence that runs immediately after construction.
   * Fetches merchant settings, persists them, then mounts consumer fragments.
   */
  private async initialize(): Promise<void> {
    try {
      const settings = await this.#orchestrator.fetchSettings();
      this.#dataState.settings.set(settings);
      logger.info('init', 'Merchant settings loaded successfully');

      this.fragments.mountConfiguredFragments();
      // Queue all initial messages needed to set state for payment method, fees & totals, etc
      this.#dataState.publishInitialState();
      this.#orchestrator.setSdkInitialized(true);
      this.#initialized = true;
    } catch (error) {
      logger.error(
        'init',
        'Failed to fetch merchant settings. Please check your configuration and network connectivity.',
        error,
      );

      this.#orchestrator.setSdkInitialized(false);
      this.#initialized = false;
    }
  }

  /**
   * Exposes the generated session ID for consumers/debugging if needed.
   */
  public getSessionId(): string {
    return this.#dataState.session.id;
  }

  /**
   * Get a specific fragment's references.
   *
   * @param fragmentName - Name of the fragment type (e.g., 'card', 'cvv', etc.)
   * @returns Fragment | null
   */
  public getFragment(fragmentName: FragmentName): Fragment | null {
    return this.fragments.getFragment(fragmentName);
  }

  /**
   * Check if a fragment is staged.
   *
   * @param fragmentName - Name of the fragment
   * @returns boolean
   */
  public isFragmentStaged(fragmentName: FragmentName): boolean {
    return this.fragments.isFragmentStaged(fragmentName);
  }

  /**
   * Check if all processable fragments are staged.
   *
   * @returns boolean
   */
  public allFragmentsStaged(): boolean {
    return this.fragments.allFragmentsStaged();
  }

  /**
   * Get all fragments that are not staged.
   *
   * @returns string[] - Array of fragment names that are not staged
   */
  public getUnstaggedFragments(): string[] {
    return this.fragments.getUnstaggedFragments();
  }

  /**
   * Send reset command to all fragments.
   *
   * @returns void
   */
  public reset(): void {
    this.fragments.reset();
  }

  /**
   * Submit all staged fragments for processing.
   *
   * @returns boolean - True if submission was initiated, false if a submission is already in progress or fragments are not ready
   */
  public submit(): boolean {
    return this.fragments.submit();
  }

  /**
   * Switch the active payment method and notify all fragments.
   *
   * @param paymentMethod - The payment method to activate
   * @returns boolean
   */
  public updatePaymentMethod(paymentMethod: PaymentMethod): boolean {
    return this.fragments.updatePaymentMethod(paymentMethod);
  }

  /**
   * Register a listener for an AndDone SDK event.
   *
   * @param event - Event type to listen for
   * @param listener - Callback invoked when the event is emitted
   * @returns void
   */
  public listen<Type extends EventType>(event: Type, listener: EventListenerFor<Type>): void {
    this.events.on(event, listener);
  }

  /**
   * Remove a previously registered event listener.
   *
   * @param event - Event type the listener is registered under
   * @param listener - Callback to remove
   * @returns void
   */
  public unlisten<Type extends EventType>(event: Type, listener: EventListenerFor<Type>): void {
    this.events.off(event, listener);
  }

  /**
   * Clean up and destroy all components.
   *
   * @returns void
   */
  public destroy(): void {
    if (!this.#initialized) return;

    this.fragments.destroy();
    this.#orchestrator.destroy();
    this.events.removeAllListeners();

    this.#initialized = false;

    logger.info('init', `Root AndDone library destroyed`);
  }
}

// Freeze the prototype so the consuming application cannot redefine public
// methods via the window.AnddoneJS handle to intercept or bypass them.
Object.freeze(AndDone.prototype);
